"use client";

import Badge from "@/components/ui/badge/Badge";
import StatCard from "@/components/ui/StatCard";
import { rmcClient } from "@/lib/rmcClient";
import {
  ArrowRightIcon,
} from "@/icons";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import React from "react";

function asNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export default function RmcDashboard() {
  const quickActions = [
    { label: "Purchase", href: "/purchase/challans/new" },
    { label: "Purchase Invoice", href: "/purchase/invoices" },
    { label: "Sales", href: "/sales/challans/new" },
    { label: "Receipt", href: "/accounts/receipts" },
    { label: "Payment", href: "/accounts/payments" },
    { label: "Ledger", href: "/accounts/ledger" },
    { label: "Current Stock View", href: "/stock" },
    { label: "Invoice", href: "/sales/invoices" },
  ];

  const { data: dashboard } = useQuery({
    queryKey: ["stockDashboard"],
    queryFn: async () => (await rmcClient.getStockDashboard()).data,
  });

  const stockRows = React.useMemo(() => {
    if (Array.isArray(dashboard?.stock_management)) return dashboard.stock_management;
    if (Array.isArray(dashboard?.current_stock_values)) return dashboard.current_stock_values;
    if (Array.isArray(dashboard?.stock_rows)) return dashboard.stock_rows;
    if (Array.isArray(dashboard?.materials)) return dashboard.materials;
    if (Array.isArray(dashboard?.stock?.rows)) return dashboard.stock.rows;
    if (Array.isArray(dashboard?.rows)) return dashboard.rows;
    if (Array.isArray(dashboard)) return dashboard;
    return [];
  }, [dashboard]);

  const stockManagementRows = React.useMemo(() => {
    return stockRows.map((row) => {
      const materialName =
        (typeof row?.material === "string" ? row.material : row?.material?.name) ||
        row?.material_name ||
        "—";
      const uom = row?.uom || row?.material?.uom || "—";
      const balance = asNumber(row?.balance ?? row?.current_qty);
      const rawStatus = String(row?.status ?? "").toLowerCase();
      const statusLabel =
        row?.status?.label ??
        row?.status ??
        (balance <= 0 ? "Low Stock" : balance <= 10 ? "Monitor" : "In Stock");
      const statusColor =
        row?.status?.color ??
        (rawStatus.includes("low") || balance <= 0
          ? "red"
          : rawStatus.includes("monitor") || balance <= 10
            ? "yellow"
            : "green");
      const isLowStock =
        String(row?.low_stock_alert ?? "").toLowerCase().includes("attention") ||
        statusColor === "red" ||
        statusColor === "yellow" ||
        balance <= 0;

      return {
        materialName,
        uom,
        balance,
        statusLabel,
        statusColor,
        isLowStock,
        lowStockAlert: row?.low_stock_alert || (isLowStock ? "Attention Required" : "Normal"),
      };
    });
  }, [stockRows]);

  const totalSalesCumValue = React.useMemo(() => {
    const directValue =
      dashboard?.total_sales_cum ??
      dashboard?.today_sales_cum ??
      dashboard?.sales_cum ??
      dashboard?.totals?.sales_cum ??
      dashboard?.totals?.total_sales_cum;
    if (directValue != null && directValue !== "") {
      return `${asNumber(directValue).toFixed(3)} CUM`;
    }

    const salesRows = Array.isArray(dashboard?.sales_challans) ? dashboard.sales_challans : [];
    if (!salesRows.length) return "—";
    const totalQty = salesRows.reduce((sum, row) => {
      if (Array.isArray(row?.items) && row.items.length) {
        const lineQty = row.items.reduce(
          (itemSum, item) =>
            itemSum + asNumber(item.qty_cum ?? item.qty ?? item.quantity ?? item.total_qty),
          0
        );
        return sum + lineQty;
      }
      return sum + asNumber(row.total_qty_cum ?? row.total_qty ?? row.qty_cum ?? row.qty);
    }, 0);
    return `${totalQty.toFixed(3)} CUM`;
  }, [dashboard]);

  const companyName = dashboard?.company_name || "Your Company";
  const activeFinancialYear = dashboard?.active_financial_year || "—";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-white">Dashboard</h1>
      </div>

      <div className="rounded-2xl border border-gray-800 bg-gray-900/40 p-4 sm:p-5">
        <p className="text-xs uppercase tracking-wide text-gray-500">Welcome</p>
        <h2 className="mt-1 text-lg font-semibold text-white">{companyName}</h2>
        <p className="mt-1 text-sm text-gray-300">
          Active Financial Year: <span className="font-medium text-white">{activeFinancialYear}</span>
        </p>
      </div>

      <div className="max-w-xs">
        <StatCard label="Total Sales (CUM)" value={totalSalesCumValue} />
      </div>

      <div className="rounded-2xl border border-gray-800 bg-gray-900/40 p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-white">Quick Actions</h2>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {quickActions.map((action) => (
            <Link
              key={action.label}
              href={action.href}
              className="flex h-9 items-center gap-2 rounded-xl border border-gray-800 bg-gray-950/70 px-3 text-sm text-gray-200 transition hover:border-brand-500/60 hover:bg-gray-900 hover:text-white"
            >
              <span className="inline-flex shrink-0 items-center justify-center text-gray-300 -translate-y-px">
                <ArrowRightIcon className="size-4" />
              </span>
              <span className="leading-none text-[13px]">{action.label}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-800 bg-gray-900/40 p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium text-white">Stock Management</h2>
          <Link href="/stock" className="text-[11px] text-gray-500 hover:text-white">
            Open stock module
          </Link>
        </div>

        {!stockManagementRows.length ? (
          <div className="rounded-xl border border-gray-800 bg-gray-950/70 px-3 py-4 text-sm text-gray-400">
            No stock materials found.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-800 bg-gray-950/40">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-950/90 text-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Material</th>
                  <th className="px-4 py-3 text-left font-semibold">UOM</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-right font-semibold">Balance</th>
                  <th className="px-4 py-3 text-right font-semibold">Low Stock Alert</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800 bg-gray-950/40">
                {stockManagementRows.slice(0, 8).map((row, index) => (
                  <tr key={`${row.materialName}-${index}`} className="text-gray-200">
                    <td className="px-4 py-3 text-left">{row.materialName}</td>
                    <td className="px-4 py-3 text-left text-gray-300">{row.uom}</td>
                    <td className="px-4 py-3 text-left">
                      <Badge
                        color={
                          row.statusColor === "red"
                            ? "error"
                            : row.statusColor === "yellow"
                              ? "warning"
                              : "success"
                        }
                      >
                        {row.statusLabel}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{row.balance.toFixed(3)}</td>
                    <td className="px-4 py-3">
                      {row.isLowStock ? (
                        <div className="flex justify-end">
                          <span className="inline-flex items-center rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-300">
                            {row.lowStockAlert}
                          </span>
                        </div>
                      ) : (
                        <div className="flex justify-end">
                          <span className="inline-flex items-center rounded-md border border-gray-700 bg-gray-800/60 px-2 py-1 text-xs text-gray-400">
                            {row.lowStockAlert}
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
